// =============================================================================
// VandieZ admin-web -- Pricing configuration (/pricing)
// =============================================================================
// AD-07 + AD-08. D-2 committed to eight pricing models "admin-configurable
// without an app release" -- this screen is what makes that true. Without it
// the catalog table is data nobody can reach, and enabling a model still
// means a developer.
//
// Two halves:
//   * the catalog     -- which of the eight models are live
//   * the fare rules  -- what each enabled model actually charges, scoped
//                        route > zone > city > vehicle type > global
//
// Rules SUPERSEDE rather than update. Saving a rule at an existing scope
// closes the old one with an effective_to and writes a new row, because an
// in-place edit silently rewrites the price of work already quoted. The
// superseded rows stay visible behind a toggle -- they are the answer to
// "what did this lane cost in June".
// =============================================================================

import {
  AlertCircle,
  Check,
  History,
  IndianRupee,
  Loader2,
  Plus,
  X,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { pricingAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  FareRule,
  FareRuleCreatePayload,
  PricingModel,
  PricingModelCode,
  TollHandling,
} from "@/types/pricing";
import { FIELD_LABELS, REQUIRED_FIELDS_BY_MODEL } from "@/types/pricing";

/** Component fields worth showing for each model. */
const FIELDS_BY_MODEL: Record<string, readonly string[]> = {
  fixed: ["flat_price_inr"],
  zone: ["flat_price_inr"],
  km: ["base_fare_inr", "per_km_rate_inr", "minimum_fare_inr"],
  time: ["base_fare_inr", "per_hour_rate_inr", "minimum_fare_inr"],
  weight: ["base_fare_inr", "per_tonne_rate_inr", "minimum_fare_inr"],
  volume: ["base_fare_inr", "per_cbm_rate_inr", "minimum_fare_inr"],
  custom: [],
  bidding: [],
};

const TOLL_OPTIONS: { value: TollHandling; label: string }[] = [
  { value: "excluded", label: "Excluded — driver recovers tolls separately" },
  { value: "included", label: "Included in the quoted price" },
  { value: "allowance", label: "Allowance — add a fixed toll amount" },
];

const EMPTY_FORM: FareRuleCreatePayload = {
  pricing_model: "km",
  free_waiting_minutes: 60,
  toll_handling: "excluded",
};

function money(value: string | null): string {
  if (value === null) return "—";
  return `₹${Number(value).toLocaleString("en-IN")}`;
}

function scopeLabel(rule: FareRule): string {
  if (rule.pickup_district && rule.drop_district) {
    return `${rule.pickup_district} → ${rule.drop_district}`;
  }
  if (rule.pickup_district) return `From ${rule.pickup_district}`;
  if (rule.vehicle_type) return `Vehicle: ${rule.vehicle_type}`;
  return "Global";
}

export function PricingPage(): React.ReactElement {
  const { accessToken } = useAuth();
  const [models, setModels] = useState<PricingModel[]>([]);
  const [rules, setRules] = useState<FareRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FareRuleCreatePayload>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!accessToken) return;
      setLoading(true);
      setError(null);
      try {
        const [modelRes, ruleRes] = await Promise.all([
          pricingAdminApi.listModels(accessToken, signal),
          pricingAdminApi.listFareRules(
            accessToken,
            { includeSuperseded: showSuperseded },
            signal,
          ),
        ]);
        setModels(modelRes.items);
        setRules(ruleRes.items);
      } catch (err) {
        if (signal?.aborted) return;
        setError(
          err instanceof ApiError ? err.message : "Could not load pricing.",
        );
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [accessToken, showSuperseded],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /// p9. Moves the recommendation; the server clears the incumbent, so
  /// the whole list is refetched rather than patching one row -- two rows
  /// changed, and patching only the one we asked about would leave the
  /// old recommendation showing its badge until the next load.
  async function recommend(model: PricingModel): Promise<void> {
    if (!accessToken) return;
    setToggling(model.code);
    setError(null);
    try {
      await pricingAdminApi.recommendModel(accessToken, model.code);
      const fresh = await pricingAdminApi.listModels(accessToken);
      setModels(fresh.items);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not set that.",
      );
    } finally {
      setToggling(null);
    }
  }

  async function toggleModel(model: PricingModel): Promise<void> {
    if (!accessToken) return;
    setToggling(model.code);
    setError(null);
    try {
      const updated = await pricingAdminApi.setModelEnabled(
        accessToken,
        model.code,
        !model.is_enabled,
      );
      setModels((prev) =>
        prev.map((m) => (m.code === updated.code ? updated : m)),
      );
    } catch (err) {
      // The server refuses two cases on purpose -- disabling bidding, and
      // disabling a model with live loads. Surface its reason verbatim
      // rather than a generic failure, because the reason is the whole
      // point.
      setError(
        err instanceof ApiError
          ? err.message
          : `Could not change ${model.display_name}.`,
      );
    } finally {
      setToggling(null);
    }
  }

  async function submitRule(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!accessToken) return;
    setSaving(true);
    setError(null);
    try {
      await pricingAdminApi.createFareRule(accessToken, form);
      setShowForm(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not save the rule.",
      );
    } finally {
      setSaving(false);
    }
  }

  const activeFields = FIELDS_BY_MODEL[form.pricing_model] ?? [];
  const requiredFields = REQUIRED_FIELDS_BY_MODEL[form.pricing_model] ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pricing"
        description="Which pricing models are live, and what each one charges."
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ---- AD-07: the catalog ------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Pricing models</CardTitle>
          <CardDescription>
            Turning a model on takes effect immediately — no release needed.
            A model with no fare rule cannot quote, so add a rule below
            before enabling one.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && models.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            models.map((model) => {
              const ruleCount = rules.filter(
                (r) => r.pricing_model === model.code && r.effective_to === null,
              ).length;
              const needsRule =
                model.is_enabled &&
                ruleCount === 0 &&
                model.code !== "bidding" &&
                model.code !== "custom";
              return (
                <div
                  key={model.code}
                  className="flex items-start justify-between gap-4 rounded-lg border p-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{model.display_name}</span>
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-xs",
                          model.is_enabled
                            ? "bg-success-subtle text-success-subtle-foreground"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        {model.is_enabled ? "Live" : "Off"}
                      </span>
                      {model.is_recommended && (
                        <span className="rounded bg-primary-subtle px-1.5 py-0.5 text-xs text-primary-subtle-foreground">
                          Recommended
                        </span>
                      )}
                      {needsRule && (
                        <span className="rounded bg-warning-subtle px-1.5 py-0.5 text-xs text-warning-subtle-foreground">
                          No fare rule — cannot quote
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {model.description}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                  {/*
                    p9. Only offered on enabled models: the shipper's picker
                    lists enabled models only, so recommending a disabled one
                    would be a setting that silently does nothing -- and the
                    server refuses it with CANNOT_RECOMMEND_DISABLED.
                  */}
                  {model.is_enabled && !model.is_recommended && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={toggling === model.code}
                      onClick={() => void recommend(model)}
                    >
                      Recommend
                    </Button>
                  )}
                  <Button
                    variant={model.is_enabled ? "outline" : "default"}
                    size="sm"
                    disabled={toggling === model.code}
                    onClick={() => void toggleModel(model)}
                  >
                    {toggling === model.code ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : model.is_enabled ? (
                      <>
                        <X className="mr-1 h-4 w-4" /> Turn off
                      </>
                    ) : (
                      <>
                        <Check className="mr-1 h-4 w-4" /> Turn on
                      </>
                    )}
                  </Button>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* ---- AD-08: fare rules -------------------------------------- */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>Fare rules</CardTitle>
            <CardDescription>
              Most specific scope wins: route, then zone, then city, then
              vehicle type, then global. Saving a rule at a scope that
              already has one replaces it — the old rule is kept for audit,
              never edited.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowForm((v) => !v)}>
            <Plus className="mr-1 h-4 w-4" /> New rule
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showForm && (
            <form
              onSubmit={(e) => void submitRule(e)}
              className="space-y-4 rounded-lg border p-4"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="pricing_model">Model</Label>
                  <select
                    id="pricing_model"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={form.pricing_model}
                    onChange={(e) =>
                      setForm({
                        ...EMPTY_FORM,
                        pricing_model: e.target.value as PricingModelCode,
                      })
                    }
                  >
                    {models
                      .filter((m) => m.code !== "bidding")
                      .map((m) => (
                        <option key={m.code} value={m.code}>
                          {m.display_name}
                        </option>
                      ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Bidding is absent on purpose — its price comes from
                    drivers, not a rule.
                  </p>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="vehicle_type">Vehicle type (optional)</Label>
                  <Input
                    id="vehicle_type"
                    placeholder="e.g. open_body — blank applies to all"
                    value={form.vehicle_type ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        vehicle_type: e.target.value || null,
                      })
                    }
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="pickup_district">From district (optional)</Label>
                  <Input
                    id="pickup_district"
                    placeholder="e.g. Chennai"
                    value={form.pickup_district ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        pickup_district: e.target.value || null,
                      })
                    }
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="drop_district">To district (optional)</Label>
                  <Input
                    id="drop_district"
                    placeholder="e.g. Coimbatore"
                    value={form.drop_district ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        drop_district: e.target.value || null,
                      })
                    }
                  />
                </div>

                {activeFields.map((field) => (
                  <div key={field} className="space-y-1">
                    <Label htmlFor={field}>
                      {FIELD_LABELS[field] ?? field}
                      {requiredFields.includes(field) && (
                        <span className="ml-1 text-destructive">*</span>
                      )}
                    </Label>
                    <Input
                      id={field}
                      type="number"
                      step="0.0001"
                      min="0"
                      required={requiredFields.includes(field)}
                      value={
                        (form[field as keyof FareRuleCreatePayload] as
                          | string
                          | undefined) ?? ""
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          [field]: e.target.value || null,
                        })
                      }
                    />
                  </div>
                ))}

                <div className="space-y-1">
                  <Label htmlFor="toll_handling">Tolls</Label>
                  <select
                    id="toll_handling"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={form.toll_handling ?? "excluded"}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        toll_handling: e.target.value as TollHandling,
                      })
                    }
                  >
                    {TOLL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                {form.toll_handling === "allowance" && (
                  <div className="space-y-1">
                    <Label htmlFor="toll_allowance_inr">
                      {FIELD_LABELS["toll_allowance_inr"]}
                      <span className="ml-1 text-destructive">*</span>
                    </Label>
                    <Input
                      id="toll_allowance_inr"
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={form.toll_allowance_inr ?? ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          toll_allowance_inr: e.target.value || null,
                        })
                      }
                    />
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  rows={2}
                  placeholder="Why this rule exists — the next admin will thank you."
                  value={form.notes ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, notes: e.target.value || null })
                  }
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <IndianRupee className="mr-1 h-4 w-4" />
                  )}
                  Save rule
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={showSuperseded}
              onChange={(e) => setShowSuperseded(e.target.checked)}
            />
            <History className="h-3.5 w-3.5" />
            Show superseded rules (what a lane used to cost)
          </label>

          {rules.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No fare rules yet. Every model except bidding needs one before
              it can quote.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4">Model</th>
                    <th className="py-2 pr-4">Scope</th>
                    <th className="py-2 pr-4">Base</th>
                    <th className="py-2 pr-4">Rate</th>
                    <th className="py-2 pr-4">Minimum</th>
                    <th className="py-2 pr-4">Tolls</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr
                      key={rule.id}
                      className={cn(
                        "border-b last:border-0",
                        rule.effective_to !== null && "opacity-60",
                      )}
                    >
                      <td className="py-2 pr-4 font-medium">
                        {rule.pricing_model}
                      </td>
                      <td className="py-2 pr-4">{scopeLabel(rule)}</td>
                      <td className="py-2 pr-4">{money(rule.base_fare_inr)}</td>
                      <td className="py-2 pr-4">
                        {money(
                          rule.per_km_rate_inr ??
                            rule.per_hour_rate_inr ??
                            rule.per_tonne_rate_inr ??
                            rule.per_cbm_rate_inr ??
                            rule.flat_price_inr,
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {money(rule.minimum_fare_inr)}
                      </td>
                      <td className="py-2 pr-4">{rule.toll_handling}</td>
                      <td className="py-2">
                        {rule.effective_to === null ? (
                          <span className="rounded bg-success-subtle px-1.5 py-0.5 text-xs text-success-subtle-foreground">
                            Active
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Superseded{" "}
                            {new Date(rule.effective_to).toLocaleDateString()}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PricingPage;
